import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { editorClient } from "@/lib/editorClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SeoSettingsForm } from "@/components/app/page-settings/SeoSettingsForm";
import { ReadingSettingsForm } from "@/components/app/page-settings/ReadingSettingsForm";
import { VersionList } from "@/components/app/page-settings/VersionList";
import { Trans } from "@lingui/react/macro";
import { languageKeys } from "@op/puck-types";
import { redirect } from "@tanstack/react-router";

const paramsSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
  pageSlug: z.string(),
});

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/settings"
)({
  params: paramsSchema,
  beforeLoad: ({
    params: { organizationSlug, projectSlug, languageKey, topicSlug, pageSlug },
  }) => {
    return {
      seoSettingsQuery: {
        queryKey: [
          "editor-page-settings-seo",
          { organizationSlug, projectSlug, languageKey, topicSlug, pageSlug },
        ],
        queryFn: async () => {
          const res = await editorClient.pages[":organizationSlug"][
            ":projectSlug"
          ][":languageKey"][":topicSlug"][":pageSlug"].settings.seo.$get({
            param: {
              organizationSlug,
              projectSlug,
              languageKey,
              topicSlug,
              pageSlug,
            },
          });
          if (!res.ok) {
            if (res.status === 401) throw redirect({ to: "/login" });
            throw new Error("Failed to fetch SEO settings");
          }
          const data = await res.json();
          return data;
        },
      },
      readingSettingsQuery: {
        queryKey: [
          "editor-page-settings-reading",
          { organizationSlug, projectSlug, languageKey, topicSlug, pageSlug },
        ],
        queryFn: async () => {
          const res = await editorClient.pages[":organizationSlug"][
            ":projectSlug"
          ][":languageKey"][":topicSlug"][":pageSlug"].settings[
            "reading-settings"
          ].$get({
            param: {
              organizationSlug,
              projectSlug,
              languageKey,
              topicSlug,
              pageSlug,
            },
          });
          if (!res.ok) {
            if (res.status === 401) throw redirect({ to: "/login" });
            throw new Error("Failed to fetch reading settings");
          }
          const data = await res.json();
          return data;
        },
      },
      versionsQuery: {
        queryKey: [
          "editor-page-settings-versions",
          { organizationSlug, projectSlug, languageKey, topicSlug, pageSlug },
        ],
        queryFn: async () => {
          const res = await editorClient.pages[":organizationSlug"][
            ":projectSlug"
          ][":languageKey"][":topicSlug"][":pageSlug"].settings.versions.$get({
            param: {
              organizationSlug,
              projectSlug,
              languageKey,
              topicSlug,
              pageSlug,
            },
          });
          if (!res.ok) {
            if (res.status === 401) throw redirect({ to: "/login" });
            throw new Error("Failed to fetch versions");
          }
          const data = await res.json();
          return data;
        },
      },
    };
  },
  loader: async ({
    context: {
      queryClient,
      seoSettingsQuery,
      readingSettingsQuery,
      versionsQuery,
    },
  }) => {
    await queryClient.prefetchQuery(seoSettingsQuery);
    await queryClient.prefetchQuery(readingSettingsQuery);
    await queryClient.prefetchQuery(versionsQuery);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { seoSettingsQuery, readingSettingsQuery, versionsQuery } =
    Route.useRouteContext();
  const { organizationSlug, projectSlug, languageKey, topicSlug, pageSlug } =
    Route.useParams();
  const queryClient = useQueryClient();
  const { data: seoSettings } = useQuery(seoSettingsQuery, queryClient);
  const { data: readingSettings } = useQuery(readingSettingsQuery, queryClient);
  const { data: versions } = useQuery(versionsQuery, queryClient);

  return (
    <div className="flex w-full max-w-[576px] mx-auto flex-col items-start gap-6 py-12">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="w-full text-heading-2 font-heading-2 text-default-font">
          <Trans>Page Settings</Trans>
        </span>
        <span className="text-body font-body text-subtext-color">
          <Trans>
            Page Settings for {topicSlug}/{pageSlug}
          </Trans>
        </span>
      </div>
      {seoSettings && (
        <SeoSettingsForm
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
          topicSlug={topicSlug}
          pageSlug={pageSlug}
          seoSettings={seoSettings}
        />
      )}
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      {readingSettings && (
        <ReadingSettingsForm
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
          topicSlug={topicSlug}
          pageSlug={pageSlug}
          readingSettings={readingSettings}
        />
      )}
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      {versions && (
        <VersionList
          versions={versions}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
          topicSlug={topicSlug}
          pageSlug={pageSlug}
        />
      )}
    </div>
  );
}
